import React, { useMemo, useState, useEffect } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  TextField,
  Typography,
  Stack,
  Divider,
} from "@mui/material";
import { format } from "date-fns";
import SearchRounded from "@mui/icons-material/SearchRounded";
import RestartAltRounded from "@mui/icons-material/RestartAltRounded";
import PictureAsPdfRounded from "@mui/icons-material/PictureAsPdfRounded";
import TableChartRounded from "@mui/icons-material/TableChartRounded";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

import {
  useGetNoReportDeputiesQuery,
  useLazyGetNoReportDeputiesQuery,
} from "../../../services/report.query";
import { DefaultPage } from "../../../layouts/default-page";
import { DataTableFive } from "../../../layouts/layout2/data-table-five";
import { MRT_SortingState } from "material-react-table";
import { useTableState } from "../../../hooks/useTableState";
import { ExportLoadingBackdrop } from "../../../components/common/ExportLoadingBackdrop";
import { Notify } from "../../../layouts/notify";
import {
  fetchAllInBatches,
  mapRowsInChunks,
  yieldToMainThread,
} from "../../../utils/exportUtils";

const formatDateOnly = (value?: string | null) => {
  if (!value) return "—";
  const [year, month, day] = value.substring(0, 10).split("-").map(Number);
  if (!year || !month || !day) return "—";
  return format(new Date(year, month - 1, day), "MMM dd, yyyy");
};

export default function NoReportDeputiesPage() {
  const defaultFilters = useMemo(
    () => ({
      deputyName: "",
      deputyControlNumber: "",
      deputyEndorser: "",
    }),
    []
  );

  const {
    page,
    pageSize,
    sortBy,
    sortOrder,
    filters,
    hasActiveFilters,
    setPage,
    setSorting: updateTableSorting,
    setFilters,
    resetFilters,
  } = useTableState(defaultFilters, {
    defaultPage: 1,
    defaultPageSize: 100,
    defaultSortBy: "card_issued_date",
    defaultSortOrder: "asc",
  });

  const [draftDeputyName, setDraftDeputyName] = useState(filters.deputyName);
  const [draftControlNumber, setDraftControlNumber] = useState(filters.deputyControlNumber);
  const [draftEndorser, setDraftEndorser] = useState(filters.deputyEndorser);

  useEffect(() => {
    setDraftDeputyName(filters.deputyName);
    setDraftControlNumber(filters.deputyControlNumber);
    setDraftEndorser(filters.deputyEndorser);
  }, [filters]);

  const sorting = useMemo<MRT_SortingState>(
    () => [{ id: sortBy, desc: sortOrder === "desc" }],
    [sortBy, sortOrder]
  );

  const { data: response, isLoading, isFetching, isError } = useGetNoReportDeputiesQuery(
    {
      page,
      rowsPerPage: pageSize,
      deputyName: filters.deputyName,
      deputyControlNumber: filters.deputyControlNumber,
      deputyEndorser: filters.deputyEndorser,
      sortColumn: sortBy || "card_issued_date",
      sortDirection: sortOrder || "asc",
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const deputies = response?.data || [];
  const totalCount = response?.count || 0;

  const handleSearch = () => {
    setFilters({
      deputyName: draftDeputyName.trim(),
      deputyControlNumber: draftControlNumber.trim(),
      deputyEndorser: draftEndorser.trim(),
    });
  };

  const handleReset = () => {
    resetFilters();
  };

  const handleEnterSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const [triggerFetchAllNoReports] = useLazyGetNoReportDeputiesQuery();
  const [isExporting, setIsExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState("");
  const [exportProgress, setExportProgress] = useState<number | null>(null);

  const fetchAllNoReportDeputies = async () => {
    return fetchAllInBatches<any>({
      batchSize: 1000,
      knownTotal: Number(totalCount || 0),
      fetchBatch: async (_offset, limit, batchPage) => {
        const res = await triggerFetchAllNoReports(
          {
            page: batchPage,
            rowsPerPage: limit,
            deputyName: filters.deputyName,
            deputyControlNumber: filters.deputyControlNumber,
            deputyEndorser: filters.deputyEndorser,
            sortColumn: sortBy || "card_issued_date",
            sortDirection: sortOrder || "asc",
          },
          false
        ).unwrap();
        return {
          rows: res?.data || [],
          total: res?.count ?? Number(totalCount || 0),
        };
      },
      onProgress: (fetched, total, percent) => {
        setExportProgress(percent);
        if (total && total > 0) {
          setExportMessage(
            `Fetching inactive deputies: ${fetched.toLocaleString()} of ${total.toLocaleString()} deputies...`
          );
        } else {
          setExportMessage(
            `Fetching inactive deputies: ${fetched.toLocaleString()} deputies loaded...`
          );
        }
      },
    });
  };

  const handleExportPDF = async () => {
    setIsExporting(true);
    setExportProgress(0);
    setExportMessage("Preparing to fetch all matching inactive deputies...");
    try {
      const allRows = await fetchAllNoReportDeputies();
      if (allRows.length === 0) {
        Notify("info", "No records found to export.");
        return;
      }

      setExportProgress(88);
      setExportMessage(
        `Formatting ${allRows.length.toLocaleString()} inactive deputies for PDF...`
      );
      await yieldToMainThread(30);

      const tableRows = await mapRowsInChunks(
        allRows,
        (row: any, idx: number) => [
          idx + 1,
          String(row.deputy_control_number || "—"),
          String(row.deputy_name || "—"),
          String(row.deputy_endorser || "—"),
          formatDateOnly(row.card_issued_date),
          row.last_report_date ? formatDateOnly(row.last_report_date) : "None",
          row.blocked_date ? formatDateOnly(row.blocked_date) : "—",
        ],
        {
          chunkSize: 1000,
          startPercent: 88,
          endPercent: 95,
          onProgress: (processed, total, percent) => {
            setExportProgress(percent);
            setExportMessage(
              `Preparing PDF rows: ${processed.toLocaleString()} of ${total.toLocaleString()}...`
            );
          },
        }
      );

      setExportProgress(96);
      setExportMessage(
        `Rendering official PDF document (${allRows.length.toLocaleString()} deputies)...`
      );
      await yieldToMainThread(40);

      const doc = new jsPDF("landscape", "mm", "a4");

      doc.setFontSize(14);
      doc.setTextColor(0, 114, 188);
      doc.text("MOVIE AND TELEVISION REVIEW AND CLASSIFICATION BOARD", 14, 15);
      doc.setFontSize(10);
      doc.setTextColor(71, 85, 105);
      doc.text("Three Consecutive Months No Report Deputies Directory", 14, 21);

      doc.setFontSize(8);
      doc.setTextColor(100, 116, 139);
      doc.text(
        `Generated: ${format(new Date(), "MMM dd, yyyy · hh:mm a")}  |  Total Inactive Deputies: ${allRows.length.toLocaleString()}`,
        14,
        27
      );

      const tableColumn = [
        "#",
        "Control Number",
        "Deputy Name",
        "Endorser",
        "Card Issued Date",
        "Last Report Date",
        "Blocked Date",
      ];

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 32,
        theme: "grid",
        headStyles: {
          fillColor: [0, 114, 188],
          textColor: [255, 255, 255],
          fontStyle: "bold",
          fontSize: 8,
        },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        styles: {
          fontSize: 7.5,
          cellPadding: 2,
          textColor: [15, 23, 42],
          overflow: "linebreak",
        },
        margin: { top: 12, left: 14, right: 14, bottom: 14 },
      });

      const totalPages = (doc as any).internal.getNumberOfPages();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(7.5);
        doc.setTextColor(148, 163, 184);
        doc.text(
          `Page ${i} of ${totalPages}`,
          pageWidth - 14,
          pageHeight - 6,
          { align: "right" }
        );
      }

      setExportProgress(100);
      setExportMessage("Download starting...");
      await yieldToMainThread(20);

      doc.save(`MTRCB_No_Report_Deputies_${format(new Date(), "yyyyMMdd_HHmm")}.pdf`);
      Notify("success", `Exported all ${allRows.length.toLocaleString()} deputies to PDF.`);
    } catch (err: any) {
      console.error("PDF export error:", err);
      Notify("error", err?.message || "Failed to generate PDF export.");
    } finally {
      setIsExporting(false);
      setExportProgress(null);
    }
  };

  const handleExportExcel = async () => {
    setIsExporting(true);
    setExportProgress(0);
    setExportMessage("Preparing to fetch all matching inactive deputies...");
    try {
      const allRows = await fetchAllNoReportDeputies();
      if (allRows.length === 0) {
        Notify("info", "No records found to export.");
        return;
      }

      setExportProgress(88);
      setExportMessage(
        `Formatting ${allRows.length.toLocaleString()} inactive deputies for Excel...`
      );
      await yieldToMainThread(30);

      const rows = await mapRowsInChunks(
        allRows,
        (item: any, idx: number) => ({
          "No.": idx + 1,
          "Control Number": item.deputy_control_number || "—",
          "Deputy Name": item.deputy_name || "—",
          "Endorser": item.deputy_endorser || "—",
          "Card Issued Date": formatDateOnly(item.card_issued_date),
          "Last Report Date": item.last_report_date ? formatDateOnly(item.last_report_date) : "None",
          "Blocked Date": item.blocked_date ? formatDateOnly(item.blocked_date) : "—",
        }),
        {
          chunkSize: 1000,
          startPercent: 88,
          endPercent: 95,
          onProgress: (processed, total, percent) => {
            setExportProgress(percent);
            setExportMessage(
              `Preparing Excel rows: ${processed.toLocaleString()} of ${total.toLocaleString()}...`
            );
          },
        }
      );

      setExportProgress(96);
      setExportMessage(
        `Writing Excel workbook (${allRows.length.toLocaleString()} deputies)...`
      );
      await yieldToMainThread(40);

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "No Report Deputies");

      ws["!cols"] = [
        { wch: 6 },
        { wch: 18 },
        { wch: 28 },
        { wch: 24 },
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
      ];

      XLSX.writeFile(wb, `MTRCB_No_Report_Deputies_${format(new Date(), "yyyyMMdd_HHmm")}.xlsx`);
      setExportProgress(100);
      setExportMessage("Download complete!");
      await yieldToMainThread(20);
      Notify("success", `Exported all ${allRows.length.toLocaleString()} deputies to Excel.`);
    } catch (err: any) {
      console.error("Excel export error:", err);
      Notify("error", err?.message || "Failed to generate Excel export.");
    } finally {
      setIsExporting(false);
      setExportProgress(null);
    }
  };

  const columns = useMemo(
    () => [
      {
        id: "rowNumber",
        header: "No.",
        size: 65,
        enableSorting: false,
        Cell: ({ row }: any) => {
          const index = (page - 1) * pageSize + row.index + 1;
          return <Typography sx={{ fontSize: 13, color: "#64748B" }}>{index}</Typography>;
        },
      },
      {
        accessorKey: "deputy_control_number",
        header: "Control Number",
        size: 160,
        Cell: ({ cell }: any) => (
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#0F172A" }}>
            {cell.getValue() || "—"}
          </Typography>
        ),
      },
      {
        id: "deputy",
        accessorKey: "deputy_name",
        header: "Deputy",
        size: 280,
        Cell: ({ row }: any) => {
          const name = row.original.deputy_name || "—";
          const profile = row.original.profile_picture;

          return (
            <Box display="flex" alignItems="center" gap={1.25}>
              <Avatar
                src={profile || ""}
                alt={name}
                sx={{ width: 32, height: 32, fontSize: 13, border: "1px solid #E2E8F0" }}
              >
                {name.charAt(0)}
              </Avatar>
              <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: "#0F172A" }}>
                {name}
              </Typography>
            </Box>
          );
        },
      },
      {
        accessorKey: "deputy_endorser",
        header: "Endorser",
        size: 220,
        Cell: ({ cell }: any) => (
          <Typography sx={{ fontSize: 13, color: "#475569" }}>{cell.getValue() || "—"}</Typography>
        ),
      },
      {
        accessorKey: "card_issued_date",
        header: "Card Issued Date",
        size: 160,
        Cell: ({ cell }: any) => (
          <Typography sx={{ fontSize: 13, color: "#334155" }}>
            {formatDateOnly(cell.getValue())}
          </Typography>
        ),
      },
      {
        accessorKey: "last_report_date",
        header: "Last Report Date",
        size: 160,
        Cell: ({ cell }: any) => (
          <Typography sx={{ fontSize: 13, color: "#334155" }}>
            {cell.getValue() ? formatDateOnly(cell.getValue()) : "None"}
          </Typography>
        ),
      },
      {
        accessorKey: "blocked_date",
        header: "Blocked Date",
        size: 150,
        Cell: ({ cell }: any) => (
          <Typography sx={{ fontSize: 13, color: "#DC2626", fontWeight: 600 }}>
            {formatDateOnly(cell.getValue())}
          </Typography>
        ),
      },
    ],
    [page, pageSize]
  );

  return (
    <DefaultPage
      title="Three Consecutive Months No Report"
      subTitle="View and track deputies missing their monthly report submissions for three consecutive months."
    >
      <Box mb={2}>
        <Alert severity="info" sx={{ borderRadius: "6px", fontSize: 13 }}>
          Displays deputies who have not submitted reports in the last three consecutive months.
        </Alert>
      </Box>

      {isError && (
        <Box mb={2}>
          <Alert severity="error" sx={{ borderRadius: "6px", fontSize: 13 }}>
            Failed to load deputies.
          </Alert>
        </Box>
      )}

      {/* Standardized Gold-Standard Filter Panel */}
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          border: "1px solid #E2E8F0",
          borderRadius: "6px",
          p: 2,
          mb: 2,
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.04)",
        }}
      >
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            flexWrap="wrap"
            useFlexGap
            alignItems={{ xs: "stretch", md: "center" }}
          >
            <TextField
              size="small"
              label="Deputy Name"
              placeholder="Search name..."
              value={draftDeputyName}
              onChange={(e) => setDraftDeputyName(e.target.value)}
              onKeyDown={handleEnterSearch}
              sx={{
                minWidth: 220,
                "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 13.5 },
              }}
            />

            <TextField
              size="small"
              label="Control Number"
              placeholder="Search control number..."
              value={draftControlNumber}
              onChange={(e) => setDraftControlNumber(e.target.value)}
              onKeyDown={handleEnterSearch}
              sx={{
                minWidth: 220,
                "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 13.5 },
              }}
            />

            <TextField
              size="small"
              label="Endorser"
              placeholder="Search endorser..."
              value={draftEndorser}
              onChange={(e) => setDraftEndorser(e.target.value)}
              onKeyDown={handleEnterSearch}
              sx={{
                minWidth: 220,
                "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 13.5 },
              }}
            />
          </Stack>

          <Divider sx={{ borderColor: "#F1F5F9" }} />

          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            spacing={1.5}
          >
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                size="small"
                startIcon={<SearchRounded fontSize="small" />}
                onClick={handleSearch}
                disabled={isFetching}
                sx={{
                  backgroundColor: "#0072BC",
                  color: "#FFFFFF",
                  fontWeight: 600,
                  fontSize: 13,
                  px: 2,
                  py: 0.75,
                  borderRadius: "4px",
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#005a96",
                    boxShadow: "none",
                  },
                }}
              >
                {isFetching ? "Searching..." : "Search"}
              </Button>

              {hasActiveFilters && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RestartAltRounded fontSize="small" />}
                  onClick={handleReset}
                  disabled={isFetching}
                  sx={{
                    color: "#64748B",
                    borderColor: "#CBD5E1",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 13,
                    borderRadius: "4px",
                    "&:hover": {
                      borderColor: "#94A3B8",
                      backgroundColor: "#F8FAFC",
                    },
                  }}
                >
                  Clear
                </Button>
              )}
            </Stack>

            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<PictureAsPdfRounded fontSize="small" />}
                onClick={handleExportPDF}
                disabled={isExporting || totalCount === 0}
                sx={{
                  color: "#334155",
                  borderColor: "#CBD5E1",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 13,
                  borderRadius: "4px",
                  "&:hover": {
                    borderColor: "#94A3B8",
                    backgroundColor: "#F8FAFC",
                  },
                }}
              >
                Export PDF
              </Button>

              <Button
                variant="outlined"
                size="small"
                startIcon={<TableChartRounded fontSize="small" />}
                onClick={handleExportExcel}
                disabled={isExporting || totalCount === 0}
                sx={{
                  color: "#0072BC",
                  borderColor: "#CBD5E1",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 13,
                  borderRadius: "4px",
                  "&:hover": {
                    borderColor: "#0072BC",
                    backgroundColor: "#F0F9FF",
                  },
                }}
              >
                Export Excel
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Box>

      {/* Matched Records Feedback */}
      <Box mb={2}>
        <Alert
          severity="success"
          sx={{
            borderRadius: "6px",
            fontSize: 13,
            border: "1px solid #BBF7D0",
            backgroundColor: "#F0FDF4",
            py: 0.5,
            "& .MuiAlert-icon": {
              color: "#00934C",
            },
          }}
        >
          Total matched records: <strong>{Number(totalCount || 0).toLocaleString()}</strong>
        </Alert>
      </Box>

      <DataTableFive
        data={deputies}
        total={totalCount}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        columns={columns as any}
        getRowId={(row: any) => String(row.deputy_id)}
        isLoading={isLoading}
        isFetching={isFetching}
        sorting={sorting}
        onSortingChange={(newSorting) => {
          if (newSorting.length > 0) {
            updateTableSorting(newSorting[0].id, newSorting[0].desc ? "desc" : "asc");
          }
        }}
      />

      <ExportLoadingBackdrop
        open={isExporting}
        message={exportMessage}
        progress={exportProgress}
      />
    </DefaultPage>
  );
}